'use client';

import { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { Moon, Sun } from 'lucide-react';
import { TINT_IDS } from '@/lib/tints';
import { KBareButton } from '@scriptera/ui/kit';

const STORAGE_KEY = 'scriptera-theme';
type Theme = 'light' | 'dark';

// Инлайн-скрипт в <head>: ставит data-theme (+ data-tint/data-acc оттеночных тем,
// см. lib/tints.ts и packages/ui/tokens.css) ДО первой отрисовки — без мигания.
// Атрибута data-nobg БОЛЬШЕ НЕТ (владелец 2026-08-06): градиенты фона удалены из CSS
// целиком, глушить нечего. Он же был источником вспышки на старте приложения — пока
// скрипт не проставил атрибут, CSS успевал нарисовать градиент.
export const themeInitScript = `(function(){var d=document.documentElement;try{var t=localStorage.getItem('${STORAGE_KEY}');if(t!=='light'&&t!=='dark'){t=window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';}d.setAttribute('data-theme',t);var ti=localStorage.getItem('scriptera-tint');if(ti&&ti!=='cream'&&/^[a-z]{3,12}$/.test(ti)){d.setAttribute('data-tint',ti);}
// Примерка облика (владелец 10.08.2026): ?skin=mono включает, ?skin=off выключает,
// выбор запоминается. Ставим ЗДЕСЬ, до гидратации, иначе React ругается на расхождение.
var SK=['mono'];var q=new URLSearchParams(location.search).get('skin');if(q&&SK.indexOf(q)>=0){localStorage.setItem('scriptera-skin',q);}else if(q==='off'){localStorage.setItem('scriptera-skin','off');}var sk=localStorage.getItem('scriptera-skin');if(sk!=='off'){d.setAttribute('data-skin','mono');}
}catch(e){d.setAttribute('data-theme','light');}})();`;

/** Страж атрибутов оформления: клиентские ре-рендеры <html> (смена локали, а с 05.08
 * пойман и худший случай — восстановление Next после ошибки при деплое с открытым
 * приложением) СТИРАЮТ атрибуты, поставленные скриптом. Раньше страж чинил их только
 * при смене локали. Теперь ПОСТОЯННЫЙ наблюдатель: атрибут исчез —
 * восстановлен из localStorage в тот же тик (наблюдатели shell/sendBarStyle следом сами
 * перешлют нативу правильные цвета). Явную смену темы/оттенка страж не трогает:
 * восстанавливает только ОТСУТСТВУЮЩИЕ атрибуты, а тумблеры пишут localStorage
 * синхронно с атрибутом. Рендерится в layout. */
export function AppearanceSync() {
  const locale = useLocale();
  useEffect(() => {
    const d = document.documentElement;
    const heal = (full: boolean) => {
      try {
        if (full || !d.getAttribute('data-theme')) {
          // Тема по устройству (владелец 26.08): пока человек не выбрал сам, берём
          // системную — ровно та же логика, что в themeInitScript выше.
          const stored = localStorage.getItem(STORAGE_KEY);
          const theme =
            stored === 'dark' || stored === 'light'
              ? stored
              : window.matchMedia?.('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
          d.setAttribute('data-theme', theme);
        }
        const ti = localStorage.getItem('scriptera-tint');
        // Снятый оттенок (напр. «Лагуна», удалена 2026-07-26) лечим на фирменный:
        // атрибут долой и localStorage подчистить, иначе стили-сироты.
        if (ti && ti !== 'cream' && TINT_IDS.has(ti)) {
          if (full || !d.getAttribute('data-tint')) d.setAttribute('data-tint', ti);
        } else if (full) {
          d.removeAttribute('data-tint');
          if (ti && !TINT_IDS.has(ti)) localStorage.removeItem('scriptera-tint');
        }
        // Примерка облика (data-skin) — такой же атрибут <html>, как тема и оттенок,
        // и её так же стирают клиентские ре-рендеры. Без этой ветки выбор «жил» до
        // первого перерисовывания и терялся после перезапуска приложения
        // (поймано владельцем 10.08.2026).
        // Схема одна и она по умолчанию: снять её можно только адресом ?skin=off
        // (значение 'off' в хранилище). Логика ОДИН В ОДИН с themeInitScript, иначе
        // страж стирал бы то, что скрипт поставил до отрисовки.
        const sk = localStorage.getItem('scriptera-skin');
        if (sk === 'off') {
          if (full) d.removeAttribute('data-skin');
        } else if (full || !d.getAttribute('data-skin')) {
          d.setAttribute('data-skin', 'mono');
        }
      } catch {
        /* приватный режим */
      }
    };
    heal(true);
    // Восстановление — микрозадачей после стирания; свои же setAttribute наблюдатель
    // видит как «атрибут на месте» и не зацикливается.
    const mo = new MutationObserver(() => heal(false));
    mo.observe(d, { attributes: true, attributeFilter: ['data-theme', 'data-tint', 'data-skin'] });
    return () => mo.disconnect();
  }, [locale]);
  return null;
}

function getTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  return (document.documentElement.getAttribute('data-theme') as Theme) ?? 'light';
}

/** Поставить тему снаружи (нативное приветствие шлёт helloTheme): атрибут + память —
 *  ровно то же, что делает тумблер ниже. */
export function applyTheme(next: Theme) {
  document.documentElement.setAttribute('data-theme', next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* приватный режим — игнорируем */
  }
}

interface ThemeToggleProps {
  labels?: { light: string; dark: string };
  // compact — иконочная кнопка для плавающей шапки лендинга (☾/☀).
  compact?: boolean;
  className?: string;
  title?: string;
}

export function ThemeToggle({ labels, compact, className, title }: ThemeToggleProps) {
  const [theme, setTheme] = useState<Theme>('light');

  useEffect(() => setTheme(getTheme()), []);

  function toggle() {
    const next: Theme = theme === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* приватный режим — игнорируем */
    }
    setTheme(next);
  }

  if (compact) {
    return (
      <KBareButton
        onClick={toggle}
        title={title}
        aria-label={title}
        className={className}
      >
        {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
      </KBareButton>
    );
  }

  return (
    <KBareButton
      onClick={toggle}
      className={
        className ??
        'rounded-md border border-line2 bg-surf px-3 py-2 text-sm font-bold text-ink shadow-sm transition hover:border-accent'
      }
    >
      {theme === 'light' ? labels?.dark : labels?.light}
    </KBareButton>
  );
}
